'use client'

import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroupContent,
  SidebarGroup,
  SidebarMenuButton,
  SidebarMenu,
  SidebarGroupLabel,
  SidebarMenuItem,
  SidebarGroupAction,
  SidebarMenuSubItem,
  SidebarMenuSub,
  SidebarMenuBadge,
} from './index'
import * as React from 'react'
import { LEFT_SIDE_NAV_WIDTH } from '../../Layout/constants'
import { iconTypes, Icon } from '../../Icon'
import {
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Plus,
  User2,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuProps,
  DropdownMenuComponent,
} from '../../DropDownMenu'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../../Collapsible'
import Link from 'next/link'
import { Button } from '../../Button'
import { cn } from '../../../../lib/utils'
import {
  ThreadsListSection,
  type ThreadItemData,
  type ThreadsListSectionProps,
} from './ThreadsListSection'
import {
  SidebarNavigationDragProvider,
  SidebarNavDropZone,
  SidebarNavNestGhostPreview,
  useSidebarNavigationDrag,
  useSidebarNavItemDrag,
  useSidebarNavNestDropTarget,
  useSidebarNavCreateNestTarget,
  sidebarNavDragClassName,
  type SidebarNavigationMeta,
  type NavigationLayoutChange,
} from './SidebarNavigationDrag'
import {
  flattenEditorTree,
  findEditorItemById,
  createNestGroupInTree,
  moveItemInTree,
  pruneEmptyGroupsFromTree,
  type NavigationEditorItem,
} from '../../RestructurableNavigationLayout'
import { ShortcutKeys } from '../../Kbd/ShortcutKeys'
import type { ShortcutKey } from '../../../../lib/keyboard-shortcuts'
import { WorkspaceMenu, type WorkspaceMenuProps } from '../../WorkspaceMenu'
import {
  PageTypeaheadPopover,
  type PageTypeaheadResult,
} from '../../PageTypeaheadPopover'
import { WorkspaceNavAddMenu } from '../../WorkspaceNavAddMenu'
import { SidebarNavigationEditProvider } from '../../WorkspaceNavItemEditDrawer'
import { SidebarNavItemMenuControl } from '../../WorkspaceNavItemMenu'

export type SidebarStructureProps = {
  content: SidebarContentProps
  header?: SidebarHeaderProps
  footer?: SidebarFooterProps
  workspaceMenu?: WorkspaceMenuProps
}

export type SidebarContentProps = {
  sections: SidebarSectionProps[]
  workplaceNavigationDrag?: {
    tree: NavigationEditorItem[]
    getSections: (tree: NavigationEditorItem[]) => SidebarSectionProps[]
    onLayoutChange: (changes: NavigationLayoutChange[]) => void
    onNestCreate: (anchorId: string, dragId: string) => void | Promise<void>
    onBeforeMove?: (input: {
      dragId: string
      targetParentId: string | null
      targetIndex: number
      navTree: NavigationEditorItem[]
    }) => Promise<boolean>
  }
  workplaceNavigationEdit?: {
    onUpdate: (input: {
      id: string
      label: string
      icon: iconTypes | null
    }) => void | Promise<void>
    onDelete: (id: string) => void | Promise<void>
    itemMenu?: import('../../WorkspaceNavItemEditDrawer').WorkspaceNavItemMenuConfig
  }
}

export type SidebarHeaderProps = {
  title?: string
  dropdown?: DropdownMenuProps
}

export type SidebarItemProps = {
  label: string
  icon?: iconTypes
  href: string
  badge?: string
  isActive?: boolean
  navigation?: SidebarNavigationMeta
  shortcut?: ShortcutKey[]
  action?: {
    label: string
    icon: iconTypes
    handleClick: () => void
  }
}

export type SidebarSectionActionProps = {
  label: string
  icon: iconTypes
  handleClick?: () => void
  typeahead?: {
    placeholder?: string
    emptyMessage?: string
    onSearch: (query: string) => Promise<PageTypeaheadResult[]>
    onSelect: (page: PageTypeaheadResult) => void | Promise<void>
  }
  menu?: {
    pageTypeahead?: {
      title?: string
      placeholder?: string
      emptyMessage?: string
      onSearch: (query: string) => Promise<PageTypeaheadResult[]>
      onSelect: (page: PageTypeaheadResult) => void | Promise<void>
      onCreateNewPage?: () => void
      createNewPageLabel?: string
    }
    addSection?: {
      title?: string
      placeholder?: string
      submitLabel?: string
      onSubmit: (label: string) => void | Promise<void>
    }
    onExportWorkspace?: () => void | Promise<void>
    onImportWorkspace?: () => void | Promise<void>
  }
}

export type SidebarSectionProps = {
  label?: string
  navigation?: SidebarNavigationMeta
  isMyWorkspaceSection?: boolean
  action?: SidebarSectionActionProps
  groups: SidebarGroupProps[]
}

export type SidebarGroupProps = {
  collapsible?: {
    label: string
    icon: iconTypes
    badge?: string
  }
  navigation?: SidebarNavigationMeta
  action?: {
    label: string
    icon: iconTypes
    onClick: () => void
  }
  items?: SidebarItemProps[]
  groups?: SidebarGroupProps[]
  entries?: Array<
    | { kind: 'link'; item: SidebarItemProps }
    | { kind: 'group'; group: SidebarGroupProps }
  >
  // New: threads list support
  threads?: {
    items: ThreadItemData[]
    selectedThreadId?: string | null
    maxVisible?: number
    onLoadMore?: () => void
    hasMore?: boolean
  }
}

export type SidebarMenuItemProps = {
  label: string
  icon?: string
  onClick?: () => void
  isActive?: boolean
  badge?: string | number
  tooltip?: string
  items?: SidebarMenuItemProps[]
}

export type SidebarFooterProps = {
  sections: SidebarSectionProps[]
}

function getGroupTreeChildIds(group: SidebarGroupProps): string[] {
  if (group.navigation?.id) {
    return [group.navigation.id]
  }

  return (group.items ?? [])
    .map((item) => item.navigation?.id)
    .filter((id): id is string => Boolean(id))
}

function getTreeDropIndexAfterGroup(
  navTree: NavigationEditorItem[],
  parentId: string | null,
  group: SidebarGroupProps,
): number {
  const childIds = getGroupTreeChildIds(group)
  if (!childIds.length) return 0

  const siblings =
    parentId === null
      ? navTree
      : findEditorItemById(navTree, parentId)?.children ?? []

  const lastId = childIds[childIds.length - 1]
  const lastIndex = siblings.findIndex((item) => item.id === lastId)
  return lastIndex === -1 ? 0 : lastIndex + 1
}

const SideBarStructure = (
  props: SidebarStructureProps & {
    side?: 'left' | 'right'
    width?: string
  },
): React.JSX.Element => {
  const normalizeWidth = (width: string | undefined) => {
    if (!width) return LEFT_SIDE_NAV_WIDTH
    return width.includes('px') || width.includes('%') ? width : `${width}px`
  }

  const sidebarWidth = normalizeWidth(props.width)

  const workplaceNavigationDrag = props.content.workplaceNavigationDrag
  const workplaceNavigationEdit = props.content.workplaceNavigationEdit
  const [navTree, setNavTree] = React.useState<NavigationEditorItem[]>(
    workplaceNavigationDrag?.tree ?? [],
  )

  React.useEffect(() => {
    if (workplaceNavigationDrag?.tree) {
      setNavTree(workplaceNavigationDrag.tree)
    }
  }, [workplaceNavigationDrag?.tree])

  const handleNavigationMove = React.useCallback(
    (dragId: string, targetParentId: string | null, targetIndex: number) => {
      if (!workplaceNavigationDrag) return

      void (async () => {
        if (workplaceNavigationDrag.onBeforeMove) {
          const proceed = await workplaceNavigationDrag.onBeforeMove({
            dragId,
            targetParentId,
            targetIndex,
            navTree,
          })
          if (!proceed) {
            return
          }
        }

        setNavTree((current) => {
          const next = pruneEmptyGroupsFromTree(
            moveItemInTree(current, dragId, targetParentId, targetIndex),
          )
          workplaceNavigationDrag.onLayoutChange(flattenEditorTree(next))
          return next
        })
      })()
    },
    [workplaceNavigationDrag, navTree],
  )

  const handleNavigationNestCreate = React.useCallback(
    async (anchorId: string, dragId: string) => {
      if (!workplaceNavigationDrag) return

      let snapshot: NavigationEditorItem[] = []
      setNavTree((current) => {
        snapshot = current
        return createNestGroupInTree(
          current,
          anchorId,
          dragId,
          `temp-group-${anchorId}`,
        )
      })

      try {
        await workplaceNavigationDrag.onNestCreate(anchorId, dragId)
      } catch {
        setNavTree(snapshot)
      }
    },
    [workplaceNavigationDrag],
  )

  const contentSections = React.useMemo(() => {
    if (!workplaceNavigationDrag) {
      return props.content.sections
    }

    const fixedSections = props.content.sections.slice(0, 1)
    const workplaceSections = workplaceNavigationDrag.getSections(navTree)
    return [...fixedSections, ...workplaceSections]
  }, [navTree, props.content.sections, workplaceNavigationDrag])

  const sidebarBody = (
    <>
      {props.header && !props.workspaceMenu && (
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              {props.header.dropdown && (
                <DropdownMenu
                  {...{
                    ...props.header.dropdown,
                    trigger: {
                      ...props.header.dropdown.trigger,
                      className: 'w-full h-12 px-0 pl-3 pr-2 -mt-1',
                      avatarClassName: '-ml-1',
                      rightIcon: 'ChevronsUpDown',
                      variant: 'ghost',
                      size: 'lg',
                    },
                  }}
                />
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
      )}
      {props.content && (
        <SidebarContent className={cn(props.workspaceMenu && 'pt-3')}>
          <SidebarNavigationDragProvider
            enabled={Boolean(workplaceNavigationDrag)}
            navTree={navTree}
            onMove={handleNavigationMove}
            onNestCreate={handleNavigationNestCreate}
          >
            <SidebarNavigationEditProvider
              enabled={Boolean(workplaceNavigationEdit)}
              onUpdate={
                workplaceNavigationEdit?.onUpdate ?? (async () => undefined)
              }
              onDelete={
                workplaceNavigationEdit?.onDelete ?? (async () => undefined)
              }
              itemMenu={workplaceNavigationEdit?.itemMenu}
            >
              {contentSections.map((section, index) => (
                <div
                  key={section.label || `content-section-${index}`}
                  className="relative w-full"
                >
                  {workplaceNavigationDrag && index > 0 ? (
                    <SidebarNavDropZone
                      overlay
                      parentId={null}
                      targetIndex={index - 1}
                    />
                  ) : null}
                  <SidebarSection {...section} isFirstSection={index === 0} />
                  {workplaceNavigationDrag &&
                  index === contentSections.length - 1 ? (
                    <SidebarNavDropZone
                      overlay
                      overlayPosition="bottom"
                      parentId={null}
                      targetIndex={Math.max(contentSections.length - 1, 0)}
                    />
                  ) : null}
                </div>
              ))}
            </SidebarNavigationEditProvider>
          </SidebarNavigationDragProvider>
        </SidebarContent>
      )}
      {props.footer && !props.workspaceMenu && (
        <SidebarFooter className="pb-1.5">
          {props.footer.sections.map((section, index) => (
            <SidebarSection
              key={section.label || `footer-section-${index}`}
              {...section}
            />
          ))}
        </SidebarFooter>
      )}
      {props.workspaceMenu && (
        <SidebarFooter className="px-2 pb-1.5">
          <WorkspaceMenu {...props.workspaceMenu} />
        </SidebarFooter>
      )}
    </>
  )

  return (
    <Sidebar
      minWidth={sidebarWidth}
      className="text-muted-foreground/90 py-1 overflow-hidden"
      side={props.side || 'left'}
    >
      {sidebarBody}
    </Sidebar>
  )
}

const SidebarSection = ({
  label,
  groups,
  action,
  navigation,
  isFirstSection = false,
  isMyWorkspaceSection = false,
}: SidebarSectionProps & { isFirstSection?: boolean }) => {
  const sectionParentId = navigation?.id ?? null
  const { navTree } = useSidebarNavigationDrag()
  const { isDragging, dragRef, canDrag } = useSidebarNavItemDrag(
    navigation,
    label,
  )
  const isSectionHeaderDraggable =
    navigation?.kind === 'section' && canDrag && Boolean(navigation)
  const sectionNestTarget = useSidebarNavNestDropTarget({
    navigationId: sectionParentId,
    isOpen: true,
  })

  return (
    <SidebarGroup className={cn('px-2 py-0', !isFirstSection && 'mt-1')}>
      {(label || action) && (
        <div
          ref={sectionNestTarget.nestDropRef}
          className={cn(
            'group/section-header relative flex flex-row items-center justify-between rounded-md transition-colors duration-200',
            sectionNestTarget.nestTargetClassName,
          )}
        >
          {label ? (
            isSectionHeaderDraggable ? (
              <SidebarGroupLabel
                ref={dragRef as React.Ref<HTMLDivElement>}
                className={cn(
                  'h-6 pl-3 pr-0 text-muted-foreground/60',
                  sidebarNavDragClassName(isDragging),
                )}
              >
                {label}
              </SidebarGroupLabel>
            ) : (
              <SidebarGroupLabel className="h-6 pl-3 pr-0 text-muted-foreground/60">
                {label}
              </SidebarGroupLabel>
            )
          ) : null}
          <div className="flex items-center gap-1">
            {sectionNestTarget.isNestReady ? (
              <span className="text-[10px] font-medium text-primary/80">
                Nest here
              </span>
            ) : null}
            {navigation && !isMyWorkspaceSection ? (
              <SidebarNavItemMenuControl
                item={navigation}
                label={navigation.label ?? label ?? 'Section'}
                icon={navigation.icon}
                canDelete={!isMyWorkspaceSection}
                showIconEditor={navigation.kind !== 'section'}
                triggerClassName="static size-5 translate-y-0 opacity-0 group-hover/section-header:opacity-100 group-focus-within/section-header:opacity-100"
              />
            ) : null}
            {action ? (
              action.menu ? (
                <WorkspaceNavAddMenu
                  ariaLabel={action.label}
                  pageTypeahead={action.menu.pageTypeahead}
                  addSection={action.menu.addSection}
                  onExportWorkspace={action.menu.onExportWorkspace}
                  onImportWorkspace={action.menu.onImportWorkspace}
                />
              ) : action.typeahead ? (
                <PageTypeaheadPopover
                  title={action.label}
                  placeholder={action.typeahead.placeholder}
                  emptyMessage={action.typeahead.emptyMessage}
                  onSearch={action.typeahead.onSearch}
                  onSelect={action.typeahead.onSelect}
                  popoverAlign="end"
                  trigger={
                    <button
                      type="button"
                      aria-label={action.label}
                      className="text-muted-foreground/60 text-xs rounded-lg p-1 hover:bg-accent/50"
                    >
                      <Icon name={action.icon} />
                    </button>
                  }
                />
              ) : (
                <button
                  type="button"
                  onClick={action.handleClick}
                  aria-label={action.label}
                  className="text-muted-foreground/60 text-xs rounded-lg p-1 hover:bg-accent/50"
                >
                  <Icon name={action.icon} />
                </button>
              )
            ) : null}
          </div>
        </div>
      )}
      <SidebarMenu>
        <SidebarNavDropZone parentId={sectionParentId} targetIndex={0} />
        {sectionParentId && sectionNestTarget.isNestReady ? (
          <SidebarNavNestGhostPreview anchorId={sectionParentId} />
        ) : null}
        {groups.map((group, index) => {
          const groupKey =
            group.collapsible?.label ||
            group.threads?.items?.[0]?.id ||
            group.items?.[0]?.label ||
            `group-${index}`
          const dropIndexAfterGroup = getTreeDropIndexAfterGroup(
            navTree,
            sectionParentId,
            group,
          )
          return (
            <React.Fragment key={groupKey}>
              {group.threads && (
                <ThreadsListItem key={`threads-${groupKey}`} group={group} />
              )}
              {group.collapsible && !group.threads && (
                <CollapsibleItem key={groupKey} group={group} />
              )}
              {!group.collapsible && !group.threads && (
                <StaticItem key={groupKey} group={group} />
              )}
              <SidebarNavDropZone
                parentId={sectionParentId}
                targetIndex={dropIndexAfterGroup}
              />
            </React.Fragment>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}

const ThreadsListItem = ({ group }: { group: SidebarGroupProps }) => {
  if (!group.threads) return null

  return (
    <div className="w-full">
      <ThreadsListSection
        threads={group.threads.items}
        selectedThreadId={group.threads.selectedThreadId}
        maxVisible={group.threads.maxVisible}
        onLoadMore={group.threads.onLoadMore}
        hasMore={group.threads.hasMore}
      />
    </div>
  )
}

const navItemButtonClassName =
  'h-7 min-h-7 gap-0 py-1 pl-3 pr-2 hover:bg-accent/50 text-muted-foreground transition-[background-color,color,opacity] duration-200 ease-out'

const navIconLabelClassName = 'flex min-w-0 w-full items-center gap-2'

const navChildSubMenuClassName =
  'mx-0 ml-2 mr-0 border-0 px-0 py-0.5 translate-x-0'

const defaultGroupIcon: iconTypes = 'FileText'

const navIconSlotClassName =
  'flex size-4 shrink-0 items-center justify-center [&_svg]:size-4'

const navExpandChevronClassName =
  'flex size-4 shrink-0 items-center justify-center text-muted-foreground/50 [&_svg]:size-3.5'

const navIconClassName = (isActive: boolean) =>
  cn('transition-colors duration-200 ease-out', {
    'text-message-background': isActive,
    'text-muted-foreground/90': !isActive,
  })

const resolveGroupIcon = (icon?: iconTypes) =>
  icon && icon !== 'ChevronDown' ? icon : defaultGroupIcon

const collapsibleGroupIconHoverClassName =
  'group-data-[state=open]/collapsible:opacity-0 group-hover/nav-row:opacity-0 group-focus-within/nav-row:opacity-0'

const collapsibleGroupChevronVisibleClassName =
  'group-data-[state=open]/collapsible:opacity-100 group-hover/nav-row:opacity-100 group-focus-within/nav-row:opacity-100'

const renderCollapsibleGroupIcon = (
  icon: iconTypes,
  isActive: boolean,
  dragRef?: ReturnType<typeof useSidebarNavItemDrag>['dragRef'],
  isDragging?: boolean,
  canDrag?: boolean,
) => (
  <span
    ref={dragRef as React.Ref<HTMLSpanElement>}
    className={cn(
      navIconSlotClassName,
      'relative',
      canDrag && 'cursor-grab touch-none active:cursor-grabbing',
      sidebarNavDragClassName(isDragging),
    )}
    onPointerDown={(event) => {
      event.stopPropagation()
    }}
  >
    <Icon
      name={icon}
      className={cn(
        navIconClassName(isActive),
        'transition-opacity duration-150',
        collapsibleGroupIconHoverClassName,
      )}
    />
    <span
      className={cn(
        navIconSlotClassName,
        'absolute inset-0 opacity-0 transition-opacity duration-150',
        collapsibleGroupChevronVisibleClassName,
      )}
      aria-hidden
    >
      <Icon
        name="ChevronRight"
        className="text-muted-foreground/50 transition-transform duration-200 ease-out group-data-[state=open]/collapsible:rotate-90"
      />
    </span>
  </span>
)

const renderNavIcon = (
  icon: iconTypes,
  isActive: boolean,
  dragRef?: ReturnType<typeof useSidebarNavItemDrag>['dragRef'],
  isDragging?: boolean,
  canDrag?: boolean,
) => (
  <span
    ref={dragRef as React.Ref<HTMLSpanElement>}
    className={cn(
      navIconSlotClassName,
      canDrag && 'cursor-grab touch-none active:cursor-grabbing',
      sidebarNavDragClassName(isDragging),
    )}
    onPointerDown={(event) => {
      event.stopPropagation()
    }}
  >
    <Icon name={icon} className={navIconClassName(isActive)} />
  </span>
)

const renderGroupChildren = (
  group: SidebarGroupProps,
  startIndex: number,
): React.ReactNode => {
  const parentId = group.navigation?.id ?? null
  const orderedEntries = group.entries ?? [
    ...(group.items ?? []).map((item) => ({ kind: 'link' as const, item })),
    ...(group.groups ?? []).map((nestedGroup) => ({
      kind: 'group' as const,
      group: nestedGroup,
    })),
  ]

  let childIndex = startIndex
  const nodes: React.ReactNode[] = []

  for (const entry of orderedEntries) {
    if (entry.kind === 'link') {
      nodes.push(
        <React.Fragment key={entry.item.navigation?.id ?? entry.item.label}>
          <DraggableSidebarNavItem item={entry.item} nested />
          <SidebarNavDropZone
            parentId={parentId}
            targetIndex={childIndex + 1}
          />
        </React.Fragment>,
      )
    } else {
      nodes.push(
        <React.Fragment
          key={entry.group.navigation?.id ?? entry.group.collapsible?.label}
        >
          <NestedCollapsibleItem group={entry.group} />
          <SidebarNavDropZone
            parentId={parentId}
            targetIndex={childIndex + 1}
          />
        </React.Fragment>,
      )
    }
    childIndex += 1
  }

  return nodes
}

const NestedCollapsibleItem = ({ group }: { group: SidebarGroupProps }) => {
  const groupId = group.navigation?.id ?? null
  const hasActiveItem = group.items?.some((item) => item.isActive)
  const { isDragging, dragRef, canDrag } = useSidebarNavItemDrag(
    group.navigation,
  )
  const { dragExpandedIds } = useSidebarNavigationDrag()
  const [isOpen, setIsOpen] = React.useState(hasActiveItem)
  const isDragExpanded = groupId ? dragExpandedIds.has(groupId) : false
  const effectiveOpen = isOpen || isDragExpanded
  const nestTarget = useSidebarNavNestDropTarget({
    navigationId: groupId,
    isOpen: effectiveOpen,
    onExpand: () => setIsOpen(true),
  })

  React.useEffect(() => {
    if (hasActiveItem) {
      setIsOpen(true)
    }
  }, [hasActiveItem])

  return (
    <Collapsible
      className="group/collapsible"
      open={effectiveOpen}
      onOpenChange={setIsOpen}
    >
      <SidebarMenuSubItem className="relative">
        <div
          ref={nestTarget.nestDropRef}
          className={cn(
            'group/nav-row relative rounded-md transition-colors duration-200',
            nestTarget.nestTargetClassName,
          )}
        >
          <CollapsibleTrigger asChild className="text-muted-foreground/90">
            <SidebarMenuButton
              className={cn(navItemButtonClassName, 'pr-8', {
                'bg-accent/20 text-message-background': hasActiveItem,
                'opacity-40': isDragging,
              })}
            >
              <span className={navIconLabelClassName}>
                {renderCollapsibleGroupIcon(
                  resolveGroupIcon(group.collapsible?.icon),
                  Boolean(hasActiveItem),
                  dragRef,
                  isDragging,
                  canDrag,
                )}
                <span className="truncate transition-colors duration-200 ease-out">
                  {group.collapsible?.label}
                </span>
              </span>
              {nestTarget.isNestReady ? (
                <span className="ml-auto pr-1 text-[10px] font-medium text-primary/80">
                  Nest here
                </span>
              ) : null}
            </SidebarMenuButton>
          </CollapsibleTrigger>
          {group.navigation ? (
            <SidebarNavItemMenuControl
              item={group.navigation}
              label={
                group.collapsible?.label ?? group.navigation.label ?? 'Group'
              }
              icon={group.collapsible?.icon ?? group.navigation.icon}
              showIconEditor={group.navigation.kind !== 'section'}
            />
          ) : null}
        </div>
        <CollapsibleContent className="overflow-hidden pb-1">
          <SidebarMenuSub className={navChildSubMenuClassName}>
            <SidebarNavDropZone
              parentId={group.navigation?.id ?? null}
              targetIndex={0}
            />
            {renderGroupChildren(group, 0)}
            {groupId ? (
              <SidebarNavNestGhostPreview anchorId={groupId} nested />
            ) : null}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  )
}

const CollapsibleItem = ({ group }: { group: SidebarGroupProps }) => {
  const groupId = group.navigation?.id ?? null
  const hasActiveItem = group.items?.some((item) => item.isActive)
  const { isDragging, dragRef, canDrag } = useSidebarNavItemDrag(
    group.navigation,
  )
  const { dragExpandedIds } = useSidebarNavigationDrag()
  const [isOpen, setIsOpen] = React.useState(hasActiveItem)
  const isDragExpanded = groupId ? dragExpandedIds.has(groupId) : false
  const effectiveOpen = isOpen || isDragExpanded
  const nestTarget = useSidebarNavNestDropTarget({
    navigationId: groupId,
    isOpen: effectiveOpen,
    onExpand: () => setIsOpen(true),
  })

  React.useEffect(() => {
    if (hasActiveItem) {
      setIsOpen(true)
    }
  }, [hasActiveItem])

  return (
    <Collapsible
      className="group/collapsible"
      open={effectiveOpen}
      onOpenChange={setIsOpen}
    >
      <SidebarMenuItem className="relative">
        <div
          ref={nestTarget.nestDropRef}
          className={cn(
            'group/nav-row relative rounded-md transition-colors duration-200',
            nestTarget.nestTargetClassName,
          )}
        >
          <CollapsibleTrigger asChild className="text-muted-foreground/90">
            <SidebarMenuButton
              className={cn(navItemButtonClassName, 'pr-8', {
                'bg-accent/20 text-message-background': hasActiveItem,
                'opacity-40': isDragging,
              })}
            >
              <span className={navIconLabelClassName}>
                {renderCollapsibleGroupIcon(
                  resolveGroupIcon(group.collapsible?.icon),
                  Boolean(hasActiveItem),
                  dragRef,
                  isDragging,
                  canDrag,
                )}
                <span className="truncate transition-colors duration-200 ease-out">
                  {group.collapsible?.label}
                </span>
              </span>
              {nestTarget.isNestReady ? (
                <span className="ml-auto pr-1 text-[10px] font-medium text-primary/80">
                  Nest here
                </span>
              ) : group.collapsible?.badge ? (
                <SidebarMenuBadge>{group.collapsible.badge}</SidebarMenuBadge>
              ) : null}
            </SidebarMenuButton>
          </CollapsibleTrigger>
          {group.navigation ? (
            <SidebarNavItemMenuControl
              item={group.navigation}
              label={
                group.collapsible?.label ?? group.navigation.label ?? 'Group'
              }
              icon={group.collapsible?.icon ?? group.navigation.icon}
              showIconEditor={group.navigation.kind !== 'section'}
            />
          ) : null}
        </div>
        <CollapsibleContent className="overflow-hidden pb-1 transform-gpu data-[state=open]:animate-slide-down data-[state=closed]:animate-slide-up">
          <SidebarMenuSub className={navChildSubMenuClassName}>
            <SidebarNavDropZone
              parentId={group.navigation?.id ?? null}
              targetIndex={0}
            />
            {renderGroupChildren(group, 0)}
            {groupId ? <SidebarNavNestGhostPreview anchorId={groupId} /> : null}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

const DraggableSidebarNavItem = ({
  item,
  nested = false,
}: {
  item: SidebarItemProps
  nested?: boolean
}) => {
  const itemId = item.navigation?.id
  const { isDragging, dragRef, canDrag } = useSidebarNavItemDrag(
    item.navigation,
  )
  const createNestTarget = useSidebarNavCreateNestTarget(item.navigation)
  const MenuItem = nested ? SidebarMenuSubItem : SidebarMenuItem
  const hasShortcut = Boolean(item.shortcut)
  const showCreateNestPreview = createNestTarget.isNestReady && itemId != null

  const linkContent = (
    <>
      {createNestTarget.isNestReady ? (
        <span className={navExpandChevronClassName}>
          <Icon
            name="ChevronRight"
            className="text-muted-foreground/90 opacity-70"
          />
        </span>
      ) : item.icon ? (
        renderNavIcon(
          item.icon,
          Boolean(item.isActive),
          dragRef,
          isDragging,
          canDrag,
        )
      ) : null}
      <span className="truncate">{item.label}</span>
    </>
  )

  const rowWrapper = (content: React.ReactNode) => (
    <div
      ref={createNestTarget.createNestDropRef}
      className={cn(
        'rounded-md transition-colors duration-200',
        createNestTarget.nestTargetClassName,
      )}
    >
      {content}
      {showCreateNestPreview && itemId ? (
        <SidebarNavNestGhostPreview anchorId={itemId} nested={nested} />
      ) : null}
    </div>
  )

  if (!hasShortcut) {
    return (
      <MenuItem className="relative">
        <div className="group/nav-row relative">
          {rowWrapper(
            <SidebarMenuButton
              asChild
              className={cn(navItemButtonClassName, {
                'pr-8': Boolean(item.navigation),
                'bg-accent/30 text-message-background hover:bg-accent/40':
                  item.isActive,
                'opacity-40': isDragging,
              })}
              data-active={item.isActive}
            >
              <Link
                href={item.href}
                className={cn(
                  navIconLabelClassName,
                  'cursor-pointer transition-colors duration-200 ease-out',
                  {
                    'text-message-background font-medium': item.isActive,
                    'text-muted-foreground/90': !item.isActive,
                  },
                )}
              >
                {linkContent}
              </Link>
            </SidebarMenuButton>,
          )}
          {createNestTarget.isNestReady ? (
            <span className="pointer-events-none absolute right-8 top-1/2 z-10 -translate-y-1/2 text-[10px] font-medium text-primary/80">
              Nest here
            </span>
          ) : null}
          {item.navigation ? (
            <SidebarNavItemMenuControl
              item={item.navigation}
              label={item.label}
              icon={item.icon}
              showIconEditor={item.navigation.kind !== 'section'}
            />
          ) : null}
        </div>
      </MenuItem>
    )
  }

  return (
    <MenuItem className="relative">
      <div className="group/nav-row relative">
        {rowWrapper(
          <SidebarMenuButton
            className={cn(
              'group/nav-item',
              navItemButtonClassName,
              'flex items-center gap-2',
              {
                'pr-8': Boolean(item.navigation),
                'bg-accent/30 text-message-background hover:bg-accent/40':
                  item.isActive,
                'opacity-40': isDragging,
              },
            )}
            data-active={item.isActive}
          >
            <Link
              href={item.href}
              className={cn(
                navIconLabelClassName,
                'min-w-0 flex-1 transition-colors duration-200 ease-out',
                {
                  'text-message-background font-medium': item.isActive,
                  'text-muted-foreground/90': !item.isActive,
                },
              )}
            >
              {linkContent}
            </Link>
            {createNestTarget.isNestReady ? (
              <span className="text-[10px] font-medium text-primary/80">
                Nest here
              </span>
            ) : null}
            {item.shortcut ? (
              <ShortcutKeys keys={item.shortcut} variant="hint" />
            ) : null}
          </SidebarMenuButton>,
        )}
        {item.navigation ? (
          <SidebarNavItemMenuControl
            item={item.navigation}
            label={item.label}
            icon={item.icon}
            showIconEditor={item.navigation.kind !== 'section'}
          />
        ) : null}
      </div>
    </MenuItem>
  )
}

const StaticItem = ({ group }: { group: SidebarGroupProps }) => {
  const parentId =
    group.navigation?.id ?? group.items?.[0]?.navigation?.parentId ?? null
  const items = group.items?.filter(
    (item) =>
      item.href && typeof item.href === 'string' && item.href.trim() !== '',
  )

  if (!items?.length) return null

  return (
    <>
      {items.map((item, index) => (
        <React.Fragment key={item.navigation?.id ?? item.label}>
          <SidebarNavDropZone parentId={parentId} targetIndex={index} />
          <DraggableSidebarNavItem item={item} />
        </React.Fragment>
      ))}
      <SidebarNavDropZone parentId={parentId} targetIndex={items.length} />
    </>
  )
}

export { SideBarStructure }
export type { ThreadItemData, WorkspaceMenuProps }
